import { Controller } from "@hotwired/stimulus"

// Autosaves a sticky's content on blur or after a typing pause, and persists
// user-driven card resizing — both through the SAME non-navigating background
// POST (Accept: application/json -> StickiesController#update responds 204).
//
// FD-588: the save MUST NOT navigate. It used to call form.requestSubmit(),
// which drove PATCH -> redirect -> Turbo morph of the index, replacing the
// focused <textarea> mid-edit: the caret jumped to the end and keystrokes typed
// during the round-trip were dropped. A background fetch never re-renders the
// field, so typing above the last line is safe.
//
// The controller lives on the CARD <div> (not the form) so it can observe/drive
// the whole card for resize; the form is a target used to build the request.
export default class extends Controller {
  static targets = ["field", "form", "handle", "width", "height"]

  // Clamp bounds are rendered from the model (Sticky::WIDTH_RANGE / HEIGHT_RANGE)
  // so the live-drag preview and the server-side clamp can't silently drift.
  // Defaults mirror the model in case the values aren't wired.
  static values = {
    minWidth: { type: Number, default: 180 },
    maxWidth: { type: Number, default: 640 },
    minHeight: { type: Number, default: 140 },
    maxHeight: { type: Number, default: 640 }
  }

  connect() {
    this.timeout = null
    this.pending = false // unsaved keystrokes since the last save
    this.resizing = null
    this.onResizeMove = this.onResizeMove.bind(this)
    this.onResizeEnd = this.onResizeEnd.bind(this)
    this.cancelResize = this.cancelResize.bind(this)
  }

  // Flush any still-pending edit on teardown. disconnect() fires after the element
  // has already left the document, so a form submit would be a silent no-op — the
  // keepalive POST preserves the last ~1.2s of typing on a back/forward restore, a
  // focus-preserving click, or a tab close within the debounce window. Any
  // in-progress resize is discarded (cancelResize restores the pre-drag size).
  disconnect() {
    clearTimeout(this.timeout)
    this.cancelResize()
    if (this.pending) this.flush()
  }

  scheduleSave() {
    this.pending = true
    clearTimeout(this.timeout)
    this.timeout = setTimeout(() => this.save(), 1200)
  }

  // Non-navigating background save (debounced content or blur). Replaces the old
  // requestSubmit() so the textarea is never re-rendered mid-edit.
  save() {
    clearTimeout(this.timeout)
    this.pending = false
    this.post({ keepalive: false })
  }

  // Teardown save. Mirrors draft_autosave_controller's keepalive flush.
  flush() {
    this.pending = false
    this.post({ keepalive: true })
  }

  // FormData of the form (even detached) serializes _method=patch from
  // form_with(method: :patch), so this POST is correctly method-overridden.
  post({ keepalive }) {
    const form = this.hasFormTarget ? this.formTarget : this.element
    const token = document.querySelector("meta[name=csrf-token]")?.content
    fetch(form.action, {
      method: "POST",
      headers: { Accept: "application/json", ...(token && { "X-CSRF-Token": token }) },
      body: new FormData(form),
      keepalive
    }).catch(() => {})
  }

  // --- Resize: a self-controlled corner drag handle -------------------------
  // A custom handle (not native CSS resize) so it can't collide with the
  // bottom-right delete control, and so only user drags persist size — a
  // ResizeObserver would also fire on responsive/grid reflow and pollute the
  // stored dimensions with layout-driven values.

  startResize(event) {
    if (this.resizing) return
    event.preventDefault()
    const card = this.element
    this.resizing = {
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      width: card.offsetWidth,
      height: card.offsetHeight,
      style: card.getAttribute("style") || "", // restored on cancel / no-op
      moved: false
    }
    try { this.handleTarget.setPointerCapture?.(event.pointerId) } catch { /* synthetic pointer */ }
    window.addEventListener("pointermove", this.onResizeMove)
    window.addEventListener("pointerup", this.onResizeEnd)
    window.addEventListener("pointercancel", this.cancelResize)
  }

  onResizeMove(event) {
    if (!this.resizing || event.pointerId !== this.resizing.pointerId) return
    this.resizing.moved = true
    const width = this.clamp(this.resizing.width + (event.clientX - this.resizing.x), this.minWidthValue, this.maxWidthValue)
    const height = this.clamp(this.resizing.height + (event.clientY - this.resizing.y), this.minHeightValue, this.maxHeightValue)
    this.element.style.width = `${width}px`
    this.element.style.height = `${height}px`
  }

  onResizeEnd(event) {
    if (!this.resizing || event.pointerId !== this.resizing.pointerId) return
    const { moved } = this.resizing
    this.endResize()
    // A stray click or a no-op drag (no pointer movement) must not POST.
    if (moved) this.persistSize()
  }

  // An interrupted gesture (pointercancel) or teardown discards the drag: restore
  // the pre-drag size so the live preview is never left unsaved, and never persist.
  cancelResize() {
    if (!this.resizing) return
    this.element.setAttribute("style", this.resizing.style)
    this.endResize()
  }

  endResize() {
    if (!this.resizing) return
    this.resizing = null
    window.removeEventListener("pointermove", this.onResizeMove)
    window.removeEventListener("pointerup", this.onResizeEnd)
    window.removeEventListener("pointercancel", this.cancelResize)
  }

  // Write the final size into the hidden fields and save through the same
  // non-navigating POST. Exposed for system tests to drive resize without
  // synthesizing native pointer drags.
  persistSize() {
    const width = this.clamp(this.element.offsetWidth, this.minWidthValue, this.maxWidthValue)
    const height = this.clamp(this.element.offsetHeight, this.minHeightValue, this.maxHeightValue)
    if (this.hasWidthTarget) this.widthTarget.value = width
    if (this.hasHeightTarget) this.heightTarget.value = height
    this.save()
  }

  clamp(value, min, max) {
    return Math.min(Math.max(Math.round(value), min), max)
  }
}
